import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  MapPin, 
  Circle, 
  Ruler, 
  Star, 
  Award,
  Phone,
  Mail,
  ExternalLink,
  CheckCircle,
  Calendar,
  X,
  TrendingUp,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import Layout from '../components/Layout';
import { GolfCourseDetails, apiService } from '../services/api';


const CourseDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const courseId = parseInt(id || '0');
  const [course, setCourse] = useState<GolfCourseDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const loadCourse = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const courseDetails = await apiService.getGolfCourseDetails(courseId);
        setCourse(courseDetails);
      } catch (error) {
        console.error('Failed to load course details:', error);
        setError(error instanceof Error ? error.message : 'Failed to load course details');
      } finally {
        setLoading(false);
      }
    };

    if (courseId && !isNaN(courseId)) {
      loadCourse();
    } else {
      setError('Invalid course ID');
      setLoading(false);
    }
  }, [courseId]);

  if (loading) {
    return (
      <Layout title="Loading Course Details...">
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-64 bg-gray-200 rounded-xl mb-8"></div>
            <div className="space-y-4">
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-20 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !course) {
    return (
      <Layout title="Course Not Found">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {error ? 'Error Loading Course' : 'Course Not Found'}
            </h1>
            <p className="text-gray-600 mb-8">
              {error || "The course you're looking for doesn't exist."}
            </p>
            <Link
              to="/courses"
              className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Courses
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      title={`${course.name} | Snag Your Tee`}
      description={`${course.description || ''} Located in ${course.location || 'BC'}. ${course.holes || 18} holes, Par ${course.par || 72}, ${course.length_yards || 0} yards.`}
    >
      {/* JSON-LD 结构化数据 */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SportsActivityLocation",
          "name": course.name,
          "description": course.description,
          "address": {
            "@type": "PostalAddress",
            "streetAddress": course.address,
            "addressLocality": course.location?.split(', ')[0] || "Vancouver",
            "addressRegion": "BC",
            "addressCountry": "CA"
          },
          "telephone": course.phone,
          "email": course.email,
          "url": course.website,
          "sport": "Golf",
          "amenityFeature": course.facilities?.map(facility => ({
            "@type": "LocationFeatureSpecification",
            "name": facility
          }))
        })}
      </script>

      <div className="container mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Link to="/courses" className="hover:text-green-600 transition-colors duration-200">
              Courses
            </Link>
            <span>/</span>
            <span className="text-gray-900 font-medium">{course.name}</span>
          </div>
        </nav>

        {/* Hero Section */}
        <div className="mb-8">
          {/* Course Image */}
          <div className="relative mb-6 group">
            <div className="aspect-[5/2] rounded-xl overflow-hidden bg-gray-200">
              <img
                src={course.images?.[currentImageIndex]?.url || course.image_url}
                alt={course.images?.[currentImageIndex]?.alt_text || course.name}
                className="w-full h-full object-cover object-center transition-all duration-300"
              />
            </div>
            
            {/* Navigation arrows - only show if multiple images */}
            {course.images && course.images.length > 1 && (
              <>
                {/* Left arrow */}
                <button
                  onClick={() => setCurrentImageIndex(prev => 
                    prev === 0 ? course.images!.length - 1 : prev - 1
                  )}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-110"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                
                {/* Right arrow */}
                <button
                  onClick={() => setCurrentImageIndex(prev => 
                    prev === course.images!.length - 1 ? 0 : prev + 1
                  )}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-110"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                
                {/* Dot indicators */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 opacity-0 group-hover:opacity-100 transition-all duration-200">
                  {course.images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                        index === currentImageIndex 
                          ? 'bg-white' 
                          : 'bg-white/50 hover:bg-white/75'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Course Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{course.name}</h1>
              <div className="flex items-center text-gray-600 mb-4">
                <MapPin className="w-5 h-5 mr-2" />
                <span>{course.location || 'Vancouver, BC'}</span>
                {course.course_type && (
                  <>
                    <span className="mx-2">•</span>
                    <div className="relative group">
                      <span className="cursor-help hover:text-gray-800 transition-colors duration-200 border-b border-dashed border-gray-400 hover:border-gray-600">{course.course_type}</span>
                      <div className="absolute top-1/2 left-full ml-2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                        <div className="bg-gray-900 text-white text-sm rounded-lg px-3 py-2 shadow-lg w-64">
                          {course.course_type === 'Parkland' && 'Golf course design featuring mature trees, water hazards, and park-like landscaping'}
                          {course.course_type === 'Links' && 'Traditional seaside course with natural terrain, few trees, and coastal winds'}
                          {course.course_type === 'Desert' && 'Course built in arid regions with desert landscaping and unique terrain challenges'}
                          {course.course_type === 'Mountain' && 'Course featuring elevated terrain, scenic mountain views, and varied elevation changes'}
                          <div className="absolute top-1/2 right-full -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <p className="text-gray-700 text-lg leading-relaxed max-w-3xl">
                {course.description}
              </p>
            </div>

            {/* Booking CTA */}
            <div className="mt-6 md:mt-0">
              {course.website && (
                <a
                  href={course.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-all duration-200 transform hover:scale-[1.02] shadow-lg"
                >
                  <Calendar className="w-5 h-5" />
                  Book Tee Time
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Course Statistics */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Course Details</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            <div className="text-center">
              <Circle className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900">{course.holes}</div>
              <div className="text-sm text-gray-600">Holes</div>
            </div>
            
            <div className="text-center">
              <Ruler className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900">{course.length_yards?.toLocaleString()}</div>
              <div className="text-sm text-gray-600">Yards</div>
            </div>
            
            <div className="text-center">
              <Star className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900">{course.par}</div>
              <div className="text-sm text-gray-600">Par</div>
            </div>
            
            <div className="text-center">
              <Award className="w-8 h-8 text-orange-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900">{course.course_rating}</div>
              <div className="text-sm text-gray-600">Rating</div>
            </div>
            
            <div className="text-center">
              <TrendingUp className="w-8 h-8 text-red-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900">{course.slope_rating}</div>
              <div className="text-sm text-gray-600">Slope</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Features & Facilities & Booking */}
          <div className="lg:col-span-2 space-y-8">
            {/* Course Features */}
            {course.features && course.features.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Course Features</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {course.features.map((feature, index) => (
                    <div key={index} className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Facilities */}
            {course.facilities && course.facilities.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Facilities & Services</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {course.facilities.map((facility, index) => (
                    <div key={index} className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{facility}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Booking Information */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Booking Information</h3>
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Public Access</span>
                  <div className="flex items-center gap-2">
                    {course.is_public ? (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="font-medium text-green-600">Open to Public</span>
                      </>
                    ) : (
                      <>
                        <X className="w-4 h-4 text-red-500" />
                        <span className="font-medium text-red-600">Members Only</span>
                      </>
                    )}
                  </div>
                </div>

                {course.booking_advance_days && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Advance Booking</span>
                    <span className="font-medium text-gray-900">
                      {course.booking_advance_days} days ahead
                    </span>
                  </div>
                )}
              </div>

              {/* Book Now CTA */}
              {course.website && (
                <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 text-center">
                  <h4 className="text-lg font-bold text-gray-900 mb-2">Ready to Play?</h4>
                  <p className="text-gray-600 mb-4">Book your tee time now and experience this amazing course!</p>
                  <a
                    href={course.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-all duration-200 transform hover:scale-[1.02] shadow-lg"
                  >
                    <Calendar className="w-5 h-5" />
                    Book Tee Time
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Contact & Info */}
          <div className="space-y-6">
            {/* Contact Information */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Contact & Location</h3>
              <div className="space-y-4">
                {course.address && (
                  <div className="flex items-start">
                    <MapPin className="w-5 h-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-gray-900">Address</div>
                      <div className="text-gray-600">{course.address}</div>
                    </div>
                  </div>
                )}
                
                {course.phone && (
                  <div className="flex items-center">
                    <Phone className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-gray-900">Phone</div>
                      <a href={`tel:${course.phone}`} className="text-blue-600 hover:text-blue-700">
                        {course.phone}
                      </a>
                    </div>
                  </div>
                )}
                
                {course.email && (
                  <div className="flex items-center">
                    <Mail className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-gray-900">Email</div>
                      <a href={`mailto:${course.email}`} className="text-blue-600 hover:text-blue-700">
                        {course.email}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Map */}
            {course.address && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Location Map</h3>
                <div className="aspect-video rounded-lg overflow-hidden bg-gradient-to-br from-green-50 to-blue-50 border-2 border-dashed border-gray-200 flex items-center justify-center">
                  <div className="text-center">
                    <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <div className="text-gray-600 mb-2">Interactive Map</div>
                    <div className="text-sm text-gray-500">Click below to view location</div>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <a
                    href={`https://www.google.com/maps/search/${encodeURIComponent(course.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors duration-200"
                  >
                    <div className="flex items-center justify-center gap-2">
                      <ExternalLink className="w-4 h-4" />
                      View in Google Maps
                    </div>
                  </a>
                  <a
                    href={`https://maps.apple.com/?q=${encodeURIComponent(course.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center px-4 py-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                  >
                    Open in Apple Maps
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Back to Courses */}
        <div className="mt-8 text-center">
          <Link
            to="/courses"
            className="inline-flex items-center gap-2 px-6 py-3 text-gray-600 hover:text-green-600 transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to All Courses
          </Link>
        </div>
      </div>
    </Layout>
  );
};

export default CourseDetailPage;